import Link from "next/link";
import { Product } from "@/api/product-api";
import { getProductDetailHref } from "@/libs/product/detail-href";
import { sizeLineNotifyProduct, sizeLineOf } from "@/libs/product/size-line";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import {
  Size,
  ThinSlashSVG,
} from "@/v2-components/cart-sheet/TactGloveOptions";
import { NotifyMe } from "@/v2-components/pages/tactsuit/NotifyMe";

/**
 * 사이즈 라인(#5149 · `libs/product/size-line.ts`) 멤버 PDP 의 「Size」 선택지.
 *
 * ★**고르면 형제 사이즈의 PDP 로 이동한다** — 같은 페이지 안에서 제품을 바꾸지 않는다. 사이즈마다
 * 별도 제품이고 각자 URL 이 살아 있어야(GMC 피드·사이트맵에 홉이 생기지 않게) 하기 때문이다.
 * 이 PDP 에는 지켜야 할 선택 상태(배지·게임키·옵션)가 없어서 이동으로 잃는 것이 없다.
 * `replace` 로 이동한다 — 사이즈를 여러 번 눌러도 뒤로가기 기록이 쌓이지 않게(`?color=` 와 같은 규칙).
 *
 * 모양은 DK3 글러브 사이즈 칩(`TactGloveOptions` 의 `Size`)을 그대로 쓴다 — 레일 64px · 선택 시
 * 파란 2px 외곽선 · 품절은 사선.
 *
 * ★**품절 사이즈는 누를 수 없다** — 다른 액세서리(색 스와치)·글러브(사이즈 칩)와 같은 동작이다. 대신 품절
 * 사이즈가 하나라도 있으면 글러브와 같은 「Not available in your size? · Notify me」 박스를 띄우고, 모달에서
 * 고른 사이즈 **그 제품**으로 알림을 건다(`sizeLineNotifyProduct` 의 `slugBySku`). 종전엔 품절 칩이 링크라
 * 품절 사이즈 PDP 로 넘어갔다(#5922 사용자 지적 · 2026-10-06). 직접 URL 로 들어온 품절 PDP 는 그 칩이
 * 선택 상태로 그대로 보인다.
 *
 * 카탈로그(`products`)는 **페이지가 SSR 로 받은 그 배열**을 넘긴다 — 레일의 다른 섹션과 같은 소스.
 * 카탈로그에 없는 사이즈(판매중지 · dev 에 미등록)는 그리지 않는다.
 */
export function SizeLineSelect({
  productSlug,
  products,
}: {
  productSlug: string;
  products: Product[];
}) {
  const { t } = useTrans(["gnb"]);
  const line = sizeLineOf(productSlug);
  if (!line) return null;

  const members = line.members
    .map((m) => ({ ...m, product: products.find((p) => p.slug === m.slug) }))
    .filter((m): m is typeof m & { product: Product } => !!m.product);
  if (members.length < 2) return null;

  const current = members.find((m) => m.slug === productSlug);
  const isSoldOut = (p: Product) =>
    !p.skuOptions.some((o) => (o.stock ?? 0) > 0);
  const soldOutMembers = members.filter((m) => isSoldOut(m.product));
  const notify =
    soldOutMembers.length > 0 ? sizeLineNotifyProduct(products, line) : null;
  // 모달에 미리 채울 사이즈 — 지금 PDP 가 품절이면 그 사이즈, 아니면 첫 품절 사이즈.
  const initialSize = (
    soldOutMembers.find((m) => m.slug === productSlug) ?? soldOutMembers[0]
  )?.option;

  return (
    <div className="flex flex-col gap-y-5">
      <h3 className="text-2xl">{`${t("Size")}: ${current?.size ?? ""}`}</h3>
      <div role="radiogroup" className="flex flex-wrap items-center gap-3">
        {members.map((m) => {
          const chosen = m.slug === productSlug;
          const soldOut = isSoldOut(m.product);
          const chip = (
            <Size
              className={cn(
                "h-16 w-16",
                chosen && "outline outline-2 -outline-offset-2 outline-blue-60",
                soldOut && "bg-opacity-20",
              )}
            >
              <span className="text-lg font-500 tracking-[0.15px]">
                {m.size}
              </span>
              {soldOut && (
                <div className="absolute inset-0 z-10 flex size-full items-center justify-center text-coolgray-20">
                  <ThinSlashSVG />
                </div>
              )}
            </Size>
          );
          const label = soldOut ? `${m.size} (${t("Sold Out")})` : m.size;
          if (soldOut && !chosen) {
            return (
              <span
                key={m.slug}
                role="radio"
                aria-checked={false}
                aria-disabled
                aria-label={label}
                className="cursor-not-allowed rounded"
              >
                {chip}
              </span>
            );
          }
          return (
            <Link
              key={m.slug}
              href={getProductDetailHref(m.slug)}
              replace
              scroll={false}
              role="radio"
              aria-checked={chosen}
              aria-label={label}
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-60 focus-visible:ring-offset-2"
            >
              {chip}
            </Link>
          );
        })}
      </div>
      {notify && (
        <div className="flex items-center justify-between rounded-lg bg-coolgray-10 p-5">
          <p className="text-base tracking-[0.25px] text-coolgray-90">
            {t("Not available in your size?")}
          </p>
          <NotifyMe
            productSlug={productSlug}
            productOverride={notify.product}
            slugBySku={notify.slugBySku}
            initialSize={initialSize}
            trigger={
              <button
                type="button"
                className="text-base font-500 tracking-[0.1px] text-blue-60 underline"
              >
                {t("Notify me")}
              </button>
            }
          />
        </div>
      )}
    </div>
  );
}
